<template>
  <div>
    <div :class="toggleClass" ref="toggleEl">
      <slot name="toggle" />
    </div>
    <div :class="['bg-white', tooltipClass]" v-show="!disabled && showTooltip">
      <div ref="tooltipEl" class="tooltip">
        <slot name="content" :close="closeTooltip" />
        <span v-if="!noArrow" class="tooltip-arrow" data-popper-arrow />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { createPopper, type Instance } from '@popperjs/core'
import type { TooltipPosition, TooltipTriggerEvent } from './types'

const props = withDefaults(
  defineProps<{
    trigger?: TooltipTriggerEvent
    disabled?: boolean
    delayOnMouseOver?: number
    delayOnMouseOut?: number
    noArrow?: boolean
    placement?: TooltipPosition
    toggleClass?: string
    tooltipClass?: string
  }>(),
  {
    trigger: 'hover',
    delayOnMouseOver: 100,
    delayOnMouseOut: 100,
    placement: 'bottom',
  },
)

const timer = ref<ReturnType<typeof setTimeout>>()

const showTooltip = ref(false)

const toggleEl = useTemplateRef('toggleEl')
const tooltipEl = useTemplateRef('tooltipEl')

const popperInstance = ref<Instance | null>()

watch(
  () => showTooltip.value,
  async (newValue: boolean) => {
    if (newValue) {
      await updateTooltip()
    }
  },
)

onMounted(async () => {
  await nextTick(() => {
    addEvents(props.trigger)
    createTooltip()
  })
})

onBeforeUnmount(() => {
  removeEvents()
  destroyTooltip()
})

function toggleTooltip() {
  if (showTooltip.value) {
    closeTooltip()
  } else {
    openTooltip()
  }
}

const emit = defineEmits(['open', 'close'])

function openTooltip() {
  emit('open')
  showTooltip.value = true
}

function closeTooltip() {
  if (showTooltip.value) {
    emit('close')
    showTooltip.value = false
  }
}

function destroyTooltip() {
  closeTooltip()

  if (popperInstance.value) {
    popperInstance.value.destroy()
    popperInstance.value = null
  }
}

function createTooltip() {
  if (!toggleEl.value || !tooltipEl.value) return
  popperInstance.value = createPopper(toggleEl.value, tooltipEl.value, {
    placement: props.placement,
    modifiers: [{ name: 'offset', options: { offset: [0, 8] } }],
  })
}

function addEvents(trigger: TooltipTriggerEvent) {
  switch (trigger) {
    case 'clickToOpen':
      return clickToOpenEvent()
    case 'clickToToggle':
      return clickToToggleEvent()
    case 'hover':
      return hoverEvent()
    case 'focus':
      return focusEvent()
  }
}

function clickToOpenEvent() {
  toggleEl.value?.addEventListener('click', openTooltip, true)
  document.addEventListener('click', handleDocumentClick, true)
}

function clickToToggleEvent() {
  toggleEl.value?.addEventListener('click', toggleTooltip, true)
  document.addEventListener('click', handleDocumentClick, true)
}

function hoverEvent() {
  toggleEl.value?.addEventListener('mouseover', handleMouseOver, true)
  tooltipEl.value?.addEventListener('mouseover', handleMouseOver, true)
  toggleEl.value?.addEventListener('mouseout', handleMouseOut, true)
  tooltipEl.value?.addEventListener('mouseout', handleMouseOut, true)
}

function focusEvent() {
  toggleEl.value?.addEventListener('focus', handleMouseOver, true)
  tooltipEl.value?.addEventListener('focus', handleMouseOver, true)
  toggleEl.value?.addEventListener('blur', handleMouseOut, true)
  tooltipEl.value?.addEventListener('blur', handleMouseOut, true)
}

function removeEvents() {
  toggleEl.value?.removeEventListener('click', openTooltip, true)
  toggleEl.value?.removeEventListener('click', toggleTooltip, true)
  toggleEl.value?.removeEventListener('mouseover', handleMouseOver, true)
  toggleEl.value?.removeEventListener('mouseout', handleMouseOut, true)
  toggleEl.value?.removeEventListener('focus', handleMouseOver, true)
  toggleEl.value?.removeEventListener('blur', handleMouseOut, true)
  document.removeEventListener('click', handleDocumentClick, true)
}

async function updateTooltip() {
  await popperInstance.value?.update()
}

function handleMouseOver() {
  clearTimeout(timer.value)
  timer.value = setTimeout(() => {
    openTooltip()
  }, props.delayOnMouseOver)
}

function handleMouseOut() {
  clearTimeout(timer.value)
  timer.value = setTimeout(() => {
    closeTooltip()
  }, props.delayOnMouseOut)
}

function handleDocumentClick(e: Event) {
  if (
    toggleEl.value?.contains(e.target as HTMLElement) ||
    tooltipEl.value?.contains(e.target as HTMLElement)
  ) {
    return
  }

  closeTooltip()
}
</script>
